import { withClickAnalytics } from "@asurion-core/eventualize-react";
import { Button, Tooltip } from "@mantine/core";
import { type JSX, useEffect } from "react";

import type { VoiceTask } from "@/sdk";

import { useAgentSdk } from "@/sdk";
import { useLogger } from "@/sdk/logging/LoggerProvider";
import { useAsyncLoading } from "@/shared-utils";

import { useControlsStore, withControlsLock } from "../controls.store";

/** Button used to leave a conference without ending the call for everyone else */
export function LeaveCallButton({ task }: { task: VoiceTask }): JSX.Element {
    const logger = useLogger({ module: "LeaveButton" });
    const agentSdk = useAgentSdk();
    const { controlLock, toggleConferencePanelOpen } = useControlsStore();

    const conferenceInProgress =
        task.conferenceStarted && task.conferenceParticipants.every(({ type }) => type === "inactive");
    const pendingConference = task.conferenceParticipants.some(({ type }) => type === "pending");

    const [isLoadingLeaveCall, leaveCall] = useAsyncLoading(
        withControlsLock(async () => {
            toggleConferencePanelOpen();
            // Ensure that everyone is off of hold before disengaging

            // Primary customer
            if (task.holdState.onHold) {
                await agentSdk.resumeCall(task);
            }

            const activeConferenceParticipants = task.conferenceParticipants.filter(({ type }) => type === "active");

            if (activeConferenceParticipants.length === 0) {
                logger.info(task.toLog(), "No conference participants found when leaving call");
                return;
            }

            // Additional participants
            const [{ participantCallId }] = activeConferenceParticipants;
            if (participantCallId) {
                await agentSdk.updateParticipant(task, participantCallId, {
                    endConferenceOnExit: false,
                    hold: false,
                });
            }
            await agentSdk.leaveConference(task);
        }),
    );

    const AnalyticButton = withClickAnalytics(Button<"button">, "Click");

    useEffect(() => {
        logger.info(
            { ...task.toLog(), isLoadingLeaveCall, controlLock, conferenceInProgress },
            "Leave button is disabled",
        );
    }, [conferenceInProgress, controlLock, isLoadingLeaveCall, logger, task]);

    return (
        <Tooltip
            fz="sm"
            zIndex="var(--mantine-priority-highest)"
            bg="dark.8"
            p="sm"
            label="Cannot leave while still calling participant"
            events={{ hover: pendingConference, focus: pendingConference, touch: false }}
        >
            <AnalyticButton
                analytics={{
                    actionId: "LeaveCallButton",
                }}
                aria-label="Leave Call"
                data-testid="call-controls-leave"
                disabled={
                    isLoadingLeaveCall ||
                    task.status !== "assigned" ||
                    controlLock ||
                    conferenceInProgress ||
                    pendingConference
                }
                loading={isLoadingLeaveCall}
                onClick={() => void leaveCall()}
                radius="xl"
                size="xs"
                fz="xs"
                color="red.9"
            >
                Leave
            </AnalyticButton>
        </Tooltip>
    );
}
